import {
  type Member as MemberProto,
  MemberRoleType,
  MemberType,
  type SearchableJob as SearchableJobProto,
} from '@taxfyle/api-internal/internal/job_pb'
import { JobProgressionStepStatus } from '@taxfyle/api-internal/internal/job_progression_pb'
import {
  SearchableBookkeepingProgressionInfo,
  SearchableConsultation,
  type SearchableJobProps,
  type SearchableWorkItem,
} from '@/features/jobs/list/SearchableJob'
import { mapJobProtoStatusToStatus } from '@/features/jobs/job-mappers'
import { DEFAULT, match } from '@/utils/pattern-match-util'
import type {
  PrimeJobProgressionFn,
  PrimeLegendSettingsFn,
} from '@/features/jobs'
import { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import { mapConsultationStatusProtoToStatus } from '@/features/jobs/consultations/consultation-mappers'
import {
  maybeParseIntervalToTimeRange,
  safeDecimalToNumber,
  safeTimestampToDate,
} from '@/utils/grpc-util'
import { BookkeepingStage as BookkeepingStageProto } from '@taxfyle/api-internal/internal/bookkeeping_pb'
import type { BookkeepingStage } from '@/features/jobs/bookkeeping/bookkeeping-stage'
import type {
  CollaboratorProps,
  CollaboratorRole,
  CollaboratorType,
} from '@/features/jobs/details/SubmittedJob'
import type { PrimeUserFn } from '@/features/iam'
import {
  mapAccessStatusProtoToProps,
  mapBookkeepingCycleProtoToBookkeepingCycle,
} from '@/features/jobs/bookkeeping/bookkeeping-progression-info-mappers'
import { getDisplayStage } from '@/features/jobs/bookkeeping/bookkeeping-display-stage'
import { mapStepStatusProtoToStepStatus } from '@/features/jobs/taxes/progression/progression-mappers'
import { mapJobScopeChangeStatusProtoToStatus } from '@/features/jobs/job-scope-changes/job-scope-changes-mappers'

export function mapSearchableJobProtoToProps(
  proto: SearchableJobProto,
  primeUser: PrimeUserFn,
  primeLegendSettings: PrimeLegendSettingsFn,
  primeJobProgression: PrimeJobProgressionFn,
): SearchableJobProps {
  const legendSettings = primeLegendSettings({
    id: proto.getLegendId(),
    version: proto.getLegendVersion(),
  })

  const consultation = proto.getConsultation()
  const bookkeepingProgressionInfo = proto.getBookkeepingProgressionInfo()

  return {
    id: proto.getId(),
    total: safeDecimalToNumber(proto.getTotal()),
    workspaceId: proto.getWorkspaceId(),
    conversationId: proto.getConversationId()?.getValue(),
    legendName: proto.getLegendName(),
    name: proto.getTitle(),
    description: proto.getDescription(),
    shortId: proto.getShortId(),
    status: mapJobProtoStatusToStatus(proto.getStatus()),
    lastActivityAt: safeTimestampToDate(proto.getDateLastActivity()),
    completedAt: proto.getDateCompleted()
      ? safeTimestampToDate(proto.getDateCompleted())
      : null,
    members: proto
      .getMembersList()
      .map((m) => mapSearchableMemberProtoToCollaboratorProps(m, primeUser)),
    isExtensionOnlyJob: proto.getIsExtensionOnlyJob(),
    legend: legendSettings,
    progressionStep: mapStepStatusProtoToStepStatus(proto.getProgressionStep()),
    progression:
      proto.getProgressionStep() !==
      JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED
        ? primeJobProgression({ id: proto.getId() })
        : JobProgression.none,
    consultation: consultation
      ? mapSearchableConsultationProtoToSearchableConsultation(consultation)
      : SearchableConsultation.default,
    bookkeepingProgressionInfo: bookkeepingProgressionInfo
      ? mapBookkeepingProgressionInfoProtoToSearchableBookkeepingProgressionInfo(
          bookkeepingProgressionInfo,
        )
      : SearchableBookkeepingProgressionInfo.default,
    jobScopeChangeStatus: mapJobScopeChangeStatusProtoToStatus(
      proto.getScopeChangeStatus(),
    ),
  }
}

export function mapSearchableMemberProtoToCollaboratorProps(
  proto: MemberProto,
  primeUser: PrimeUserFn,
): CollaboratorProps {
  return {
    type: mapSearchableMemberTypeProtoToType(proto.getType()),
    role: mapSearchableMemberRoleProtoToRole(proto.getRole()),
    user: primeUser({ id: proto.getId() }),
  }
}

export function mapSearchableMemberTypeProtoToType(
  proto: MemberType,
): CollaboratorType {
  return match(proto, {
    [MemberType.MEMBER_TYPE_PROVIDER]: () => 'Pro',
    [MemberType.MEMBER_TYPE_CLIENT]: () => 'Client',
    [MemberType.MEMBER_TYPE_UNSPECIFIED]: () => 'Client',
  })
}

export function mapSearchableMemberRoleProtoToRole(
  proto: MemberRoleType,
): CollaboratorRole {
  return match(proto, {
    [MemberRoleType.SUPPORTING]: () => 'Supporting',
    [MemberRoleType.CHAMPION]: () => 'Champion',
    [DEFAULT]: () => 'Champion',
  })
}

export function mapSearchableConsultationProtoToSearchableConsultation(
  proto: SearchableJobProto.Consultation,
): SearchableConsultation {
  return {
    id: proto.getId(),
    status: mapConsultationStatusProtoToStatus(proto.getStatus()),
    bookedTime: maybeParseIntervalToTimeRange(proto.getBookedTime()),
    providerUserPublicId: proto.getProviderUserPublicId(),
    roomId: proto.getRoomId(),
  }
}

export function mapBookkeepingProgressionInfoProtoToSearchableBookkeepingProgressionInfo(
  proto: SearchableJobProto.BookkeepingProgressionInfo,
): SearchableBookkeepingProgressionInfo {
  const accessStatus = mapAccessStatusProtoToProps(proto.getAccessStatus())
  const stage = mapSearchableBookkeepingProgressionInfoProtoStageToStage(
    proto.getStage(),
  )
  const cycles = proto
    .getCyclesList()
    .map(mapBookkeepingCycleProtoToBookkeepingCycle)

  const displayStage = getDisplayStage({
    stage: stage,
    accessStatus: accessStatus,
    cycles: cycles,
  })

  return {
    id: proto.getId(),
    stage: stage,
    workItems: proto.getWorkItemsList().map(mapWorkItem),
    accessStatus: accessStatus,
    cycles: cycles,
    displayStage: displayStage,
  }
}

function mapSearchableBookkeepingProgressionInfoProtoStageToStage(
  proto: BookkeepingStageProto,
): BookkeepingStage {
  return match(proto, {
    [BookkeepingStageProto.BOOKKEEPING_STAGE_UNSPECIFIED]: () => 'Unspecified',
    [BookkeepingStageProto.PENDING]: () => 'Pending',
    [BookkeepingStageProto.PROVISIONING_ACCESS]: () => 'ProvisioningAccess',
    [BookkeepingStageProto.ASSESSING_BOOKS]: () => 'AssessingBooks',
    [BookkeepingStageProto.READY_TO_START]: () => 'ReadyToStart',
    [BookkeepingStageProto.CYCLE_IN_PROGRESS]: () => 'CycleInProgress',
    [BookkeepingStageProto.COMPLETED]: () => 'Completed',
    [BookkeepingStageProto.SCOPE_EXTENSION_REQUESTED]: () =>
      'ScopeExtensionRequested',
    [DEFAULT]: () => 'Unspecified',
  })
}

function mapWorkItem(proto: SearchableJobProto.WorkItem): SearchableWorkItem {
  return {
    id: proto.getId(),
    quantity: proto.getQuantity(),
    completedCount: proto.getCompletedCount(),
  }
}
